/**
 * "뉴스 후보 큐" — review queue for automatically detected news candidates
 * (ABC/SBS 등, functions/api/internal/cron/news-collect.ts가 하루 두 번 수집).
 *
 * The backend for this already existed (functions/api/admin/news-candidates/*)
 * but had no admin screen, so there was no way to actually do the
 * "하루 2~3건만 관리자가 선택" workflow the news relaunch plan calls for -
 * candidates just piled up in `detected` status with nothing acting on them.
 *
 * This screen changes `news_candidates.status` (selected/rejected/duplicate)
 * via the existing PATCH endpoint, and - for `selected` candidates - can call
 * POST /api/admin/news-candidates/:id/generate to produce a Korean article
 * draft grounded only in that candidate's real title/excerpt/source_url
 * (functions/api/admin/news-candidates/[id]/generate.ts). The draft is always
 * created with status='pending' in the normal articles table; it still needs
 * a human to review it and click Approve in ArticlesManagement (which adds
 * things this step deliberately does not invent, like an image) before it
 * goes live. This screen never publishes anything itself.
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Copy, ExternalLink, Newspaper, Sparkles, XCircle } from "lucide-react";
import { apiGet, apiPatch, apiPost } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AdminPageLayout from "./AdminPageLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type CandidateStatus = "detected" | "selected" | "generated" | "rejected" | "duplicate" | "published";

type NewsCandidate = {
  id: string;
  source_id: string;
  source_name: string;
  source_url: string;
  source_published_at: string | null;
  source_language: string;
  title: string;
  excerpt: string | null;
  relevance_score: number;
  status: CandidateStatus;
  detected_at: string;
  updated_at: string;
};

type CandidatesResponse = {
  ok: true;
  data: NewsCandidate[];
  counts: { status: CandidateStatus; count: number }[];
};

type CoverageResponse = {
  ok: true;
  publishedByState: { state: string; count: number; latestPublishedAt: string | null }[];
  candidatesBySource: { sourceName: string; status: CandidateStatus; count: number }[];
  candidateCounts: { status: CandidateStatus; count: number }[];
};

type GenerateResponse =
  | { ok: true; method: "unavailable"; reason: string }
  | {
      ok: true;
      method: "ai";
      articleId: number;
      candidateId: string;
      publicationAudit: { ok: boolean; issues: string[] };
      draft: { titleKo: string; summaryKo: string; category: string | null };
    };

const statusOptions: { value: CandidateStatus | "all"; label: string }[] = [
  { value: "detected", label: "미검토 (신규)" },
  { value: "all", label: "전체" },
  { value: "selected", label: "선택됨" },
  { value: "rejected", label: "거절됨" },
  { value: "duplicate", label: "중복" },
  { value: "generated", label: "기사 생성됨" },
  { value: "published", label: "발행됨" },
];
const statusLabel: Record<string, string> = Object.fromEntries(statusOptions.map((o) => [o.value, o.label]));

export default function NewsCandidatesQueue() {
  const { toast } = useToast();
  const [status, setStatus] = useState<CandidateStatus | "all">("detected");

  const list = useQuery({
    queryKey: ["admin-news-candidates", status],
    queryFn: () => apiGet<CandidatesResponse>(`/api/admin/news-candidates?status=${status}&limit=100`),
  });

  const coverage = useQuery({
    queryKey: ["admin-news-coverage"],
    queryFn: () => apiGet<CoverageResponse>("/api/admin/news-coverage"),
  });

  const patch = useMutation({
    mutationFn: ({ id, status: newStatus }: { id: string; status: "selected" | "rejected" | "duplicate" }) =>
      apiPatch(`/api/admin/news-candidates/${id}`, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-news-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-news-coverage"] });
      toast({ title: "후보 상태를 변경했습니다" });
    },
    onError: (error: Error) => toast({ title: "변경 실패", description: error.message, variant: "destructive" }),
  });

  const generate = useMutation({
    mutationFn: (id: string) => apiPost<GenerateResponse>(`/api/admin/news-candidates/${id}/generate`, {}),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["admin-news-candidates"] });
      queryClient.invalidateQueries({ queryKey: ["admin-news-coverage"] });
      if (res.method === "unavailable") {
        toast({ title: "AI 초안 생성 불가", description: res.reason, variant: "destructive" });
        return;
      }
      const missing = res.publicationAudit.issues;
      toast({
        title: `초안 생성 완료 (기사 #${res.articleId})`,
        description:
          missing.length > 0
            ? `기사 관리 화면에서 검수하세요. 발행 전 보완 필요: ${missing.join(", ")}`
            : "기사 관리 화면에서 검수 후 승인하세요.",
      });
    },
    onError: (error: Error) => toast({ title: "초안 생성 실패", description: error.message, variant: "destructive" }),
  });

  const items = list.data?.data || [];
  const counts = list.data?.counts || [];
  return (
    <AdminPageLayout
      title="뉴스 후보 큐"
      description="ABC·SBS 등에서 자동 수집된 뉴스 후보를 검토합니다. 선택된 후보는 'AI 초안 생성'으로 한국어 기사 초안을 만들 수 있고, 실제 발행 승인은 기사 관리 화면에서 사람이 확인 후 진행합니다."
      icon={<Newspaper className="w-8 h-8" />}
      loading={list.isLoading}
      error={list.error ? (list.error as Error).message : null}
    >
      <div className="grid gap-4 md:grid-cols-3">
        {counts.map((c) => (
          <Card key={c.status}>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{c.count}</div>
              <p className="text-xs text-muted-foreground">{statusLabel[c.status] || c.status}</p>
            </CardContent>
          </Card>
        ))}
        {counts.length === 0 && !list.isLoading && (
          <Card className="md:col-span-3">
            <CardContent className="pt-6 text-sm text-muted-foreground">
              아직 수집된 후보가 없습니다.
            </CardContent>
          </Card>
        )}
      </div>

      {coverage.error && (
        <Card className="border-destructive/50">
          <CardContent className="pt-6 text-sm text-destructive">
            커버리지 통계를 불러오지 못했습니다: {(coverage.error as Error).message}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <Select
              value={status}
              onValueChange={(value) => setStatus(value as CandidateStatus | "all")}
            >
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {status === "detected" && (
              <p className="text-sm text-muted-foreground">
                하루 2~3건만 골라 선택하세요. 나머지는 다음 검토 때 그대로 남습니다.
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>제목 / 요약</TableHead>
                  <TableHead>출처</TableHead>
                  <TableHead>관련도</TableHead>
                  <TableHead>원문 발행 / 수집</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead className="text-right">작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="max-w-md">
                      <div className="font-medium">{c.title}</div>
                      {c.excerpt && (
                        <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{c.excerpt}</div>
                      )}
                      <a
                        href={c.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-primary underline"
                      >
                        원문 보기 <ExternalLink className="h-3 w-3" />
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{c.source_name}</Badge>
                      <div className="mt-1 text-xs text-muted-foreground">{c.source_language}</div>
                    </TableCell>
                    <TableCell className="text-sm">{c.relevance_score.toFixed(2)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      <div>{c.source_published_at ? new Date(c.source_published_at).toLocaleString("ko-KR") : "원문 일시 미상"}</div>
                      <div className="mt-1">수집 {new Date(c.detected_at).toLocaleString("ko-KR")}</div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={c.status === "detected" ? "default" : "secondary"}>
                        {statusLabel[c.status] || c.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {(c.status === "detected" || c.status === "selected") && (
                        <div className="flex justify-end gap-1">
                          {c.status !== "selected" && (
                            <Button
                              size="sm"
                              disabled={patch.isPending}
                              onClick={() => patch.mutate({ id: c.id, status: "selected" })}
                            >
                              <CheckCircle2 className="mr-1 h-3 w-3" />선택
                            </Button>
                          )}
                          {c.status === "selected" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              disabled={generate.isPending}
                              onClick={() => generate.mutate(c.id)}
                            >
                              <Sparkles className="mr-1 h-3 w-3" />
                              {generate.isPending ? "생성 중…" : "AI 초안 생성"}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={patch.isPending}
                            onClick={() => patch.mutate({ id: c.id, status: "duplicate" })}
                          >
                            <Copy className="mr-1 h-3 w-3" />중복
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={patch.isPending}
                            onClick={() => patch.mutate({ id: c.id, status: "rejected" })}
                          >
                            <XCircle className="mr-1 h-3 w-3" />거절
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!list.isLoading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                      해당 조건의 후보가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">주(State)별 발행 현황 (최근 90일)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-sm text-muted-foreground">
            호주 각 주 소식이 한국어로 얼마나 다뤄지고 있는지 확인하는 용도입니다. 특정 주가 비어 있다면
            뉴스 후보 수집 소스에 그 주의 매체를 추가하는 것을 검토하세요.
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>주(State)</TableHead>
                  <TableHead>발행 기사 수</TableHead>
                  <TableHead>최근 발행일</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(coverage.data?.publishedByState || []).map((row) => (
                  <TableRow key={row.state}>
                    <TableCell>
                      <Badge variant={row.state === "unspecified" ? "outline" : "secondary"}>
                        {row.state === "unspecified" ? "미지정" : row.state}
                      </Badge>
                    </TableCell>
                    <TableCell>{row.count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.latestPublishedAt ? new Date(row.latestPublishedAt).toLocaleDateString("ko-KR") : "-"}
                    </TableCell>
                  </TableRow>
                ))}
                {!coverage.isLoading && (coverage.data?.publishedByState.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-6 text-center text-muted-foreground">
                      최근 90일 내 발행된 기사가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">수집 소스별 후보 현황</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>소스</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>건수</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(coverage.data?.candidatesBySource || []).map((row, i) => (
                  <TableRow key={`${row.sourceName}-${row.status}-${i}`}>
                    <TableCell>{row.sourceName}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{statusLabel[row.status] || row.status}</Badge>
                    </TableCell>
                    <TableCell>{row.count}</TableCell>
                  </TableRow>
                ))}
                {!coverage.isLoading && (coverage.data?.candidatesBySource.length ?? 0) === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="py-6 text-center text-muted-foreground">
                      수집된 후보가 없습니다.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </AdminPageLayout>
  );
}
